import { resolveDepositGeoBucket } from '@/constants/deposit-geo-defaults';

/** @typedef {import('@/constants/deposit-geo-defaults').DepositGeoBucket} DepositGeoBucket */

function normalizeChainKey(chain) {
  return String(chain ?? '')
    .trim()
    .toUpperCase();
}

function normalizeCurrencySymbol(currency) {
  return String(currency ?? '')
    .trim()
    .toUpperCase();
}

function isUsdcSymbol(sym) {
  return sym === 'USDC' || sym === 'USDC.E' || sym === 'USDCE';
}

function isUsdtSymbol(sym) {
  return sym === 'USDT';
}

function pickPreferredUsdcCurrencyIdx(currencies) {
  const list = Array.isArray(currencies) ? currencies : [];
  const usdcIdx = list.findIndex((row) => isUsdcSymbol(normalizeCurrencySymbol(row?.currency)));
  return usdcIdx >= 0 ? usdcIdx : 0;
}

function pickPreferredUsdtCurrencyIdx(currencies) {
  const list = Array.isArray(currencies) ? currencies : [];
  const usdtIdx = list.findIndex((row) => isUsdtSymbol(normalizeCurrencySymbol(row?.currency)));
  if (usdtIdx >= 0) return usdtIdx;
  return pickPreferredUsdcCurrencyIdx(list);
}

function chainMatchesBase(key) {
  return key.includes('BASE');
}

function chainMatchesEth(key) {
  return key.includes('ETH') || key.includes('ETHEREUM');
}

function chainMatchesPolygon(key) {
  return key.includes('POLYGON') || key.includes('MATIC');
}

function chainMatchesArbitrum(key) {
  return key.includes('ARBITRUM') || key === 'ARB' || key.startsWith('ARB_');
}

function chainMatchesTron(key) {
  return key.includes('TRON') || key.includes('TRX');
}

function chainMatchesBsc(key) {
  return key.includes('BSC') || key.includes('BNB');
}

const USDC_ETH_CHAIN_MATCHERS = [
  chainMatchesEth,
  chainMatchesArbitrum,
  chainMatchesBase,
  chainMatchesPolygon,
  chainMatchesTron,
];

/** @type {Record<DepositGeoBucket, Array<(key: string) => boolean>>} */
const BUCKET_CHAIN_MATCHERS = {
  'usdc-eth': USDC_ETH_CHAIN_MATCHERS,
  'usdc-polygon': [chainMatchesPolygon, chainMatchesEth, chainMatchesArbitrum, chainMatchesBase],
  'usdt-tron': [chainMatchesTron, chainMatchesBsc, chainMatchesEth],
  'usdt-bsc': [chainMatchesBsc, chainMatchesTron, chainMatchesEth],
  fallback: USDC_ETH_CHAIN_MATCHERS,
};

/** @type {Record<DepositGeoBucket, 'usdc' | 'usdt'>} */
const BUCKET_TOKEN_PREFERENCE = {
  'usdc-eth': 'usdc',
  'usdc-polygon': 'usdc',
  'usdt-tron': 'usdt',
  'usdt-bsc': 'usdt',
  fallback: 'usdc',
};

function findChainIdxByMatchers(options, matchers) {
  const list = Array.isArray(options) ? options : [];
  if (!list.length) return 0;

  for (const matcher of matchers) {
    const idx = list.findIndex((row) => matcher(normalizeChainKey(row?.chain)));
    if (idx >= 0) return idx;
  }

  return 0;
}

function findChainIdx(options, bucket) {
  const matchers = BUCKET_CHAIN_MATCHERS[bucket] ?? BUCKET_CHAIN_MATCHERS.fallback;
  return findChainIdxByMatchers(options, matchers);
}

function findTokenIdx(currencies, bucket) {
  const list = Array.isArray(currencies) ? currencies : [];
  if (!list.length) return 0;

  const preference = BUCKET_TOKEN_PREFERENCE[bucket] ?? 'usdc';
  return preference === 'usdt'
    ? pickPreferredUsdtCurrencyIdx(list)
    : pickPreferredUsdcCurrencyIdx(list);
}

/**
 * 按国家分桶匹配 Cobo 充值默认链/币 index。
 * @param {Array<{ chain?: string, currencies?: Array<{ currency?: string }> }>} options
 * @param {string | null | undefined} countryCode
 * @returns {{ chainIdx: number, tokenIdx: number, currencies: Array }}
 */
export function applyCoboTransferGeoDefaults(options, countryCode) {
  const list = Array.isArray(options) ? options : [];
  if (!list.length) {
    return { chainIdx: 0, tokenIdx: 0, currencies: [] };
  }

  const bucket = resolveDepositGeoBucket(countryCode);
  const chainIdx = findChainIdx(list, bucket);
  const currencies = list[chainIdx]?.currencies ?? [];
  const tokenIdx = findTokenIdx(currencies, bucket);

  return { chainIdx, tokenIdx, currencies };
}
